import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/axios';
import MovieForm from './MovieForm';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { captureError, getUserMessage, getLocalErrors } from '../../utils/sentry';

const MovieList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editMovie, setEditMovie] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
  }, []);

  // Error handling for movie fetching
  const { data: moviesData = { movies: [], total: 0 }, isLoading, error } = useQuery({
    queryKey: ['admin-movies', page, rowsPerPage, searchQuery],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await api.get(`/api/admin/movies`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            page: page + 1,
            limit: rowsPerPage,
            search: searchQuery.trim()
          }
        });
        return {
          movies: response.data.movies || [],
          total: response.data.total || 0
        };
      } catch (error) {
        captureError(error, {
          component: 'MovieList',
          action: 'fetchMovies',
          params: { page, rowsPerPage, searchQuery }
        });
        throw error;
      }
    },
    keepPreviousData: true
  });

  // Show error state if query fails
  if (error) {
    const userMessage = getUserMessage(error);
    const debugInfo = getLocalErrors(error);
    
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {userMessage}
        </Alert>
        {debugInfo && (
          <Paper sx={{ p: 2, mt: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" color="error">Debug Information:</Typography>
            <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </Paper>
        )}
      </Box>
    );
  }

  const handleInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearch = () => {
    console.log('Performing search with:', searchInput);
    setSearchQuery(searchInput);
    setPage(0);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(0);
  };

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/api/admin/movies/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-movies']);
      setSnackbar({
        open: true,
        message: 'Movie deleted successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      console.error('Delete error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete movie',
        severity: 'error'
      });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (movieData) => {
      const token = localStorage.getItem('adminToken');
      await api.put(`/api/admin/movies/${movieData._id}`, movieData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-movies']);
      setEditMovie(null);
      setSnackbar({
        open: true,
        message: 'Movie updated successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      // Log the error to Sentry/local logger
      captureError(error, {
        component: 'MovieList',
        action: 'updateMovie',
        movieId: editMovie?._id
      });
      
      // Show user-friendly message
      setSnackbar({
        open: true,
        message: getUserMessage(error),
        severity: 'error'
      });

      // In development, log the local errors to console
      if (process.env.NODE_ENV === 'development') {
        console.log('Local error log:', getLocalErrors());
      }
    }
  });

  const handleEditSubmit = async (movieData) => {
    try {
      await updateMutation.mutateAsync(movieData);
    } catch (error) {
      console.error('Update submission error:', error);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const debounce = (func, timeout) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  };

  const handleEdit = (movie) => {
    console.log('Original movie data:', movie);
    
    // Get complete movie data for editing
    const token = localStorage.getItem('adminToken');
    api.get(`/api/admin/movies/${movie._id}/edit`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(response => {
      const movieData = response.data;
      console.log('Complete movie data:', movieData);
      
      const formattedMovie = {
        ...movieData,
        _id: movieData._id,
        title: movieData.title || '',
        description: movieData.description || '',
        releaseDate: movieData.releaseDate ? new Date(movieData.releaseDate).toISOString().split('T')[0] : '',
        categories: movieData.categories || [],
        genres: movieData.genres || [],
        movieLanguage: movieData.movieLanguage || '',
        duration: movieData.duration || '',
        quality: movieData.quality || '',
        size: movieData.size || '',
        director: movieData.director || '',
        year: movieData.year || new Date().getFullYear(),
        cast: movieData.cast || [],
        rating: movieData.rating || 0,
        featured: Boolean(movieData.featured),
        watchLinks: movieData.watchLinks || [{ quality: '', url: '' }],
        downloadLinks: movieData.downloadLinks || [],
        images: movieData.images || []
      };

      console.log('Formatted movie data for edit:', formattedMovie);
      setEditMovie(formattedMovie);
    }).catch(error => {
      console.error('Error fetching complete movie data:', error);
      // Fallback to using existing data if fetch fails
      const formattedMovie = {
        ...movie,
        _id: movie._id,
        title: movie.title || '',
        description: movie.description || '',
        releaseDate: movie.releaseDate ? new Date(movie.releaseDate).toISOString().split('T')[0] : '',
        categories: movie.categories || [],
        genres: movie.genres || [],
        movieLanguage: movie.movieLanguage || '',
        duration: movie.duration || '',
        quality: movie.quality || '',
        size: movie.size || '',
        director: movie.director || '',
        year: movie.year || new Date().getFullYear(),
        cast: movie.cast || [],
        rating: movie.rating || 0,
        featured: Boolean(movie.featured),
        watchLinks: movie.watchLinks || [{ quality: '', url: '' }],
        downloadLinks: movie.downloadLinks || [],
        images: movie.images || []
      };
      setEditMovie(formattedMovie);
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this movie?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search movies..."
          value={searchInput}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button 
          variant="contained" 
          onClick={handleSearch}
          disabled={!searchInput.trim()}
        >
          Search
        </Button>
        {searchQuery && (
          <Button 
            variant="outlined" 
            onClick={clearSearch}
          >
            Clear
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="movies table">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Downloads</TableCell>
              <TableCell>Release Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {moviesData.movies.map((movie) => (
              <TableRow key={movie._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {movie.images?.[0]?.url ? (
                      <img
                        src={movie.images[0].url}
                        alt={movie.title}
                        style={{ 
                          width: 40, 
                          height: 40, 
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          bgcolor: 'grey.200',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        No img
                      </Box>
                    )}
                    <Typography>{movie.title}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{movie.views || 0}</TableCell>
                <TableCell>{movie.downloads || 0}</TableCell>
                <TableCell>
                  {movie.releaseDate ? new Date(movie.releaseDate).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEdit(movie)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(movie._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={moviesData.total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog
        open={Boolean(editMovie)}
        onClose={() => setEditMovie(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Movie</DialogTitle>
        <DialogContent>
          {editMovie && (
            <MovieForm
              mode="edit"
              initialData={editMovie}
              onSubmit={handleEditSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MovieList;
